import React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../../../lib/utils'
import { textVariants } from '../../../utils/layoutVariants'

export interface TextProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof textVariants> {
  as?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'span'
    | 'div'
    | 'code'
    | 'pre'
  children: React.ReactNode
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      className,
      variant,
      size,
      weight,
      textColor,
      align,
      truncate,
      as,
      children,
      ...props
    },
    ref
  ) => {
    // Determine the element type based on variant or as prop
    const getElementType = () => {
      if (as) return as

      switch (variant) {
        case 'title':
          return 'h1'
        case 'subtitle':
          return 'h2'
        case 'h1':
          return 'h1'
        case 'h2':
          return 'h2'
        case 'h3':
          return 'h3'
        case 'code':
          return 'code'
        case 'kicker':
        case 'hero':
        case 'display':
        case 'small':
        case 'bodyLanding':
        case 'bodyFeature':
        case 'micro':
        case 'body':
        case 'caption':
        default:
          return 'p'
      }
    }

    const Component = getElementType()

    return (
      <Component
        ref={ref}
        className={cn(
          textVariants({
            variant,
            size,
            weight,
            textColor,
            align,
            truncate,
          }),
          className
        )}
        {...props}
      >
        {children}
      </Component>
    )
  }
)
Text.displayName = 'Text'

export { Text }
